import { Transaction, TransactionType } from "@/types/transaction";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { useMemo } from "react";
import { isPast, isToday, isWithinInterval } from "date-fns";
import { cn } from "@/lib/utils";
import { CardFilterType } from "@/hooks/useTransactionFilters";
import { formatCurrency } from '@/lib/utils/currencyUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TransactionSummaryCardsProps {
  transactions: Transaction[] | undefined;
  appliedDateRange: DateRange | undefined;
  transactionType: TransactionType | undefined;
  onCardClick: (filterType: CardFilterType) => void;
  activeCardFilter: CardFilterType;
}

export function TransactionSummaryCards({
  transactions,
  appliedDateRange,
  transactionType,
  onCardClick,
  activeCardFilter,
}: TransactionSummaryCardsProps) {

  const { vencidos, vencemHoje, aVencer, pagos, totalPeriodo } = useMemo(() => {
    let vencidos = 0;
    let vencemHoje = 0;
    let aVencer = 0;
    let pagos = 0;
    let totalPeriodo = 0;

    const today = new Date();

    const transactionsInPeriod = transactions?.filter(transaction => {
      if (transactionType && transaction.type !== transactionType) return false;

      if (!appliedDateRange?.from) return true; // If no start date, include all transactions
      if (!appliedDateRange?.to) { // If only start date, include transactions on or after start date
        return transaction.date >= appliedDateRange.from;
      }
      // If both start and end dates, include transactions within the interval
      return isWithinInterval(transaction.date, {
        start: appliedDateRange.from,
        end: appliedDateRange.to,
      });
    }) || [];

    transactionsInPeriod.forEach(transaction => {
      if (transaction.is_paid) {
        pagos += transaction.amount;
      } else { // Pending transactions
        if (isPast(transaction.date) && !isToday(transaction.date)) {
          vencidos += transaction.amount;
        } else if (isToday(transaction.date)) {
          vencemHoje += transaction.amount;
        } else { // isFuture(transaction.date) or today but covered by vencemHoje
          aVencer += transaction.amount;
        }
      }
      totalPeriodo += transaction.amount; // Total considers both paid and pending in the period
    });

    return { vencidos, vencemHoje, aVencer, pagos, totalPeriodo };
  }, [transactions, appliedDateRange, transactionType]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(amount);
  };

  const paidCardTitle = transactionType === 'income' ? 'Recebidos' : 'Pagos';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 mb-6">
      {/* Vencidos - Red/Alert */}
      <div
        className={cn(
          "border border-border text-card-foreground shadow-sm cursor-pointer transition-all duration-200 card-elevated rounded-none dark:bg-muted/50 dark:border-border/50",
          "min-h-[102px] min-w-[220px] p-4 relative border-b-4",
          activeCardFilter === 'vencidos'
            ? "border-b-red-500 bg-red-500/10 dark:bg-red-500/20"
            : "border-b-transparent hover:bg-accent/50 dark:hover:bg-accent/30"
        )}
        onClick={() => onCardClick('vencidos')}
      >
        <div className="flex flex-col justify-between h-full">
          <span className={cn(
            "text-sm font-semibold uppercase tracking-wide",
            activeCardFilter === 'vencidos' ? "dark:text-white text-red-700" : "text-red-700"
          )}>Vencidos</span>
          <div className={cn(
            "text-2xl font-bold mt-1",
            activeCardFilter === 'vencidos' ? "dark:text-white text-red-700" : "text-red-700"
          )}>{formatCurrency(vencidos)}</div>
        </div>
      </div>

      {/* Vencem Hoje - Orange/Warning */}
      <div
        className={cn(
          "border border-border text-card-foreground shadow-sm cursor-pointer transition-all duration-200 card-elevated rounded-none dark:bg-muted/50 dark:border-border/50",
          "min-h-[102px] min-w-[220px] p-4 relative border-b-4",
          activeCardFilter === 'vencemHoje'
            ? "border-b-orange-500 bg-orange-500/10 dark:bg-orange-500/20"
            : "border-b-transparent hover:bg-accent/50 dark:hover:bg-accent/30"
        )}
        onClick={() => onCardClick('vencemHoje')}
      >
        <div className="flex flex-col justify-between h-full">
          <span className={cn(
            "text-sm font-semibold uppercase tracking-wide",
            activeCardFilter === 'vencemHoje' ? "dark:text-white text-orange-700" : "text-orange-700"
          )}>Vencem Hoje</span>
          <div className={cn(
            "text-2xl font-bold mt-1",
            activeCardFilter === 'vencemHoje' ? "dark:text-white text-orange-700" : "text-orange-700"
          )}>{formatCurrency(vencemHoje)}</div>
        </div>
      </div>

      {/* A Vencer - Neutral */}
      <div
        className={cn(
          "border border-border text-card-foreground shadow-sm cursor-pointer transition-all duration-200 card-elevated rounded-none dark:bg-muted/50 dark:border-border/50",
          "min-h-[102px] min-w-[220px] p-4 relative border-b-4",
          activeCardFilter === 'aVencer'
            ? "border-b-primary bg-primary/5 dark:bg-primary/20"
            : "border-b-transparent hover:bg-accent/50 dark:hover:bg-accent/30"
        )}
        onClick={() => onCardClick('aVencer')}
      >
        <div className="flex flex-col justify-between h-full">
          <span className={cn(
            "text-sm font-semibold uppercase tracking-wide",
            activeCardFilter === 'aVencer' ? "dark:text-white text-muted-foreground" : "text-muted-foreground"
          )}>A Vencer</span>
          <div className={cn(
            "text-2xl font-bold mt-1",
            activeCardFilter === 'aVencer' ? "dark:text-white text-foreground" : "text-foreground"
          )}>{formatCurrency(aVencer)}</div>
        </div>
      </div>

      {/* Pagos/Recebidos - Green/Success */}
      <div
        className={cn(
          "border border-border text-card-foreground shadow-sm cursor-pointer transition-all duration-200 card-elevated rounded-none dark:bg-muted/50 dark:border-border/50",
          "min-h-[102px] min-w-[220px] p-4 relative border-b-4",
          activeCardFilter === 'pagos'
            ? "border-b-green-500 bg-green-500/10 dark:bg-green-500/20"
            : "border-b-transparent hover:bg-accent/50 dark:hover:bg-accent/30"
        )}
        onClick={() => onCardClick('pagos')}
       >
        <div className="flex flex-col justify-between h-full">
          <span className={cn(
            "text-sm font-semibold uppercase tracking-wide",
            activeCardFilter === 'pagos' ? "dark:text-white text-green-700" : "text-green-700"
          )}>{paidCardTitle}</span>
          <div className={cn(
            "text-2xl font-bold mt-1",
            activeCardFilter === 'pagos' ? "dark:text-white text-green-700" : "text-green-700"
          )}>{formatCurrency(pagos)}</div>
        </div>
      </div>

      {/* Total Período - Primary/Emphasis */}
      <div
        className={cn(
          "border border-border text-card-foreground shadow-sm cursor-pointer transition-all duration-200 card-elevated rounded-none dark:bg-muted/50 dark:border-border/50",
          "min-h-[102px] min-w-[220px] p-4 relative border-b-4",
          activeCardFilter === 'total'
            ? "border-b-accent bg-accent/10 dark:bg-accent/20"
            : "border-b-transparent hover:bg-accent/50 dark:hover:bg-accent/30"
        )}
        onClick={() => onCardClick('total')}
       >
        <div className="flex flex-col justify-between h-full">
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm font-semibold uppercase tracking-wide",
              activeCardFilter === 'total' ? "dark:text-white text-primary" : "text-primary"
            )}>Total Período</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn(
                    "text-xs cursor-help",
                    activeCardFilter === 'total' ? "dark:text-white/80 text-muted-foreground" : "text-muted-foreground"
                  )}>ⓘ</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Soma de todas as transações no período selecionado</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className={cn(
            "text-2xl font-bold mt-1",
            activeCardFilter === 'total' ? "dark:text-white text-primary" : "text-primary"
          )}>{formatCurrency(totalPeriodo)}</div>
        </div>
      </div>
    </div>
  );
} 
